import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from 'prop-types'; // Temporary util until aggregations can do categoryIds.

/**
 * Translates the breadcrumb data from breadcrumb API endpoint into a structure
 * similar to aggregations from products/v2. The field it uses is `categoryIds`.
 * @param {Object} activeCategory
 * @returns {Object} breadcrumb data as aggregration data
 */

export function categoryBreadcrumbToFilterCategory(activeCategory) {
  var label = activeCategory.label,
      subcategories = activeCategory.subcategories;
  var items = subcategories.filter(category => category.filterable).map(category => ({
    value: category.productCategoryId,
    label: category.label,
    filterCount: null
  }));

  if (!items.length) {
    // There should be no `categoryIds` filter in this case.
    return null;
  }

  return {
    field: 'categoryIds',
    category: activeCategory,
    label: "".concat(label, " Style"),
    // Just a default, can be customized.
    totalAggregationCount: null,
    items
  };
}
export var getTotalFilterCountCustomFilters = (filterSettings, filters) => {
  var _filters$categoryIds = filters.categoryIds,
      categoryIds = _filters$categoryIds === void 0 ? [] : _filters$categoryIds,
      _filters$aggregationF = filters.aggregationFilter,
      aggregationFilter = _filters$aggregationF === void 0 ? {} : _filters$aggregationF;
  return filterSettings.reduce((runningCount, filterSetting) => {
    var getCount = filterSetting.getCount;

    if (getCount) {
      return filterSetting.getCount({
        currentCount: runningCount,
        filters,
        filterSetting
      });
    } else {
      return runningCount + aggregationFilter[filterSetting.field].length;
    }
  }, categoryIds.length);
};
export var getTotalFilterCount = filters => {
  var _filters$categoryIds2 = filters.categoryIds,
      categoryIds = _filters$categoryIds2 === void 0 ? [] : _filters$categoryIds2,
      _filters$aggregationF2 = filters.aggregationFilter,
      aggregationFilter = _filters$aggregationF2 === void 0 ? {} : _filters$aggregationF2,
      avgReviewMin = filters.avgReviewMin,
      vipUnitPriceMinimum = filters.vipUnitPriceMinimum,
      vipUnitPriceMaximum = filters.vipUnitPriceMaximum,
      saleUnitPriceMinimum = filters.saleUnitPriceMinimum,
      saleUnitPriceMaximum = filters.saleUnitPriceMaximum;
  var totalCount = categoryIds.length;

  for (var field in aggregationFilter) {
    totalCount += aggregationFilter[field].length;
  }

  if (avgReviewMin != null) {
    totalCount += 1;
  } // Even though there are two fields, price range counts as one filter.


  if (vipUnitPriceMinimum != null || vipUnitPriceMaximum != null || saleUnitPriceMinimum != null || saleUnitPriceMaximum != null) {
    totalCount += 1;
  }

  return totalCount;
};
export var getCategoryTree = function getCategoryTree(activeCategory) {
  var showActiveSubcategories = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  var showSiblingCategories = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  var activeCategoryChecker = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : category => category.productCategoryId;
  var isTopLevel = activeCategory.isTopLevel || activeCategory.siblings.some(category => category.isTopLevel);

  if (!showActiveSubcategories) {
    // removing activeCategory's subcategories if showActiveSubcategories is false
    var _activeCategory = activeCategory,
        subcategories = _activeCategory.subcategories,
        modifiedActiveCategory = _objectWithoutProperties(_activeCategory, ["subcategories"]);

    activeCategory = modifiedActiveCategory;
  }

  var siblings = activeCategory.siblings.map(sibling => activeCategoryChecker(sibling) === activeCategoryChecker(activeCategory) ? activeCategory : sibling);

  if (!showSiblingCategories) {
    // removing sibling categories if showSiblingCategories is false
    var _activeCategory3 = activeCategory,
        _siblings = _activeCategory3.siblings,
        _modifiedActiveCategory = _objectWithoutProperties(_activeCategory3, ["siblings"]);

    activeCategory = _modifiedActiveCategory;
  }

  var activeSiblings = showSiblingCategories ? siblings : [activeCategory];
  var hasSubcategories = Boolean(activeCategory.subcategories && activeCategory.subcategories.length); // If the active category is top-level, only show its siblings if there are
  // no subcategories to list. Otherwise, the only item visible would be the
  // single active category.

  var tree = isTopLevel && hasSubcategories ? [activeCategory] : activeSiblings;

  if (!isTopLevel) {
    var parent = activeCategory.ancestors[0];

    if (parent) {
      tree = [_objectSpread(_objectSpread({}, parent), {}, {
        isAncestor: true,
        subcategories: tree
      })];

      if (!parent.isTopLevel) {
        var grandParent = activeCategory.ancestors[1];

        if (grandParent) {
          var parentSiblings = parent.siblings ? parent.siblings.map(sibling => activeCategoryChecker(parent) === activeCategoryChecker(sibling) ? tree[0] : sibling) : [];
          tree = [_objectSpread(_objectSpread({}, grandParent), {}, {
            isAncestor: true,
            subcategories: parentSiblings
          })];
        }
      }
    }
  }

  return tree;
};
export var getTopLevelCategory = activeCategory => {
  var isTopLevel = activeCategory.isTopLevel || activeCategory.siblings.some(category => category.isTopLevel);
  var topLevelCategory = activeCategory;

  if (!isTopLevel) {
    var parent = activeCategory.ancestors[0];

    if (parent) {
      topLevelCategory = _objectSpread(_objectSpread({}, parent), {}, {
        isAncestor: true
      });

      if (!parent.isTopLevel) {
        var grandParent = activeCategory.ancestors[1];

        if (grandParent) {
          topLevelCategory = _objectSpread(_objectSpread({}, grandParent), {}, {
            isAncestor: true
          });
        }
      }
    }
  }

  return topLevelCategory;
};
export var categoryPropType = PropTypes.shape({
  isAncestor: PropTypes.bool,
  label: PropTypes.node,
  productCategoryId: PropTypes.number,
  // Circular, don't think PropTypes can do that.
  subcategories: PropTypes.array,
  urlPath: PropTypes.string
});